/**
 * Detection indicator (08 §2.7, §4): hidden while nobody is noticing the ranger, an amber `?`
 * that grows with the strongest detection meter, and a red `!` once anyone is engaging him.
 */

import './detectionGlyph.css';
import type { Enemy } from '../../sim/types';
import { clamp, el, show, Shown } from './dom';

const DETECTION_MIN = 0.15;

export class DetectionGlyph {
  readonly el: HTMLElement;
  private readonly mark: HTMLElement;
  private readonly shown = new Shown();

  constructor() {
    this.el = el('span', 'hud-eye hud-hidden');
    const shape = el('span', 'hud-eye-shape');
    shape.append(el('span', 'hud-eye-pupil'));
    this.mark = el('span', 'hud-eye-mark', '?');
    this.el.append(shape, this.mark);
  }

  update(enemies: readonly Enemy[]): void {
    let detection = 0;
    let engaged = false;
    for (const enemy of enemies) {
      // Out of the fight: dead, incapacitated, or knocked down.
      if (enemy.state === 'dead' || enemy.state === 'wounded' || enemy.state === 'down') continue;
      if (enemy.detection > detection) detection = enemy.detection;
      if (enemy.seesRanger && enemy.state === 'combat') engaged = true;
    }
    const mode = engaged ? 2 : detection > DETECTION_MIN ? 1 : 0;
    if (this.shown.changed('mode', mode)) {
      show(this.el, mode !== 0);
      this.el.classList.toggle('hud-eye-alert', mode === 2);
      this.mark.textContent = mode === 2 ? '!' : '?';
    }
    const level = Math.round(clamp(detection, 0, 1) * 10);
    if (mode === 1 && this.shown.changed('level', level)) {
      this.el.style.setProperty('--hud-det', String(level / 10));
    }
  }
}
